import type { BurndownView } from "../../shared/types/api";

const WIDTH = 640;
const HEIGHT = 220;
const PADDING = { top: 12, right: 12, bottom: 28, left: 40 };

/**
 * The sprint burndown (§103).
 *
 * <p>Three lines, and each earns its place. <strong>Remaining</strong> is what the team has left.
 * <strong>Ideal</strong> descends from the commitment to zero — drawn from the commitment rather
 * than from current scope, so a sprint that took on extra work reads as behind rather than as
 * still on track. <strong>Scope</strong> is the ceiling: when it rises, work was added, and
 * without it a remaining line that goes up looks like a broken chart.
 *
 * <p>Hand-drawn SVG rather than a charting library. This is three polylines and two axes; a
 * dependency would cost more in bundle size than it saves in code, and would style itself rather
 * than following the theme.
 */
export function BurndownChart({ data }: { data: BurndownView }) {
  const points = data.points;

  if (points.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-ink-500">
        Nothing recorded yet. The chart fills in from the day the sprint starts.
      </p>
    );
  }

  // Projected once, so every read below is a plain lookup in the chosen unit rather than a
  // ternary repeated at each use site.
  const byPoints = data.basis === "STORY_POINTS";
  const remainingSeries = points.map((point) =>
    byPoints ? point.remainingStoryPoints : point.remainingHours,
  );
  const scopeSeries = points.map((point) =>
    byPoints ? point.scopeStoryPoints : point.scopeHours,
  );

  const remaining = (index: number) => remainingSeries[index] ?? 0;
  const scope = (index: number) => scopeSeries[index] ?? 0;

  // The vertical scale has to cover the tallest of the three lines, or an added-scope spike
  // would be clipped and the chart would understate exactly the thing it exists to show.
  const ceiling = Math.max(
    data.startingTotal,
    ...points.map((_, index) => Math.max(remaining(index), scope(index))),
    1,
  );

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const step = points.length === 1 ? 0 : plotWidth / (points.length - 1);

  const x = (index: number) => PADDING.left + index * step;
  const y = (value: number) => PADDING.top + plotHeight * (1 - value / ceiling);

  const path = (value: (index: number) => number) =>
    points.map((_, index) => `${x(index)},${y(value(index))}`).join(" ");

  const gridValues = [0, 0.25, 0.5, 0.75, 1].map((fraction) => ceiling * fraction);
  const unit = byPoints ? "points" : "hours";

  return (
    <figure className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="h-56 w-full min-w-[520px]"
        role="img"
        aria-label={`Burndown in ${unit}: ${remaining(points.length - 1)} remaining of ${data.startingTotal} committed`}
      >
        {gridValues.map((value) => (
          <g key={value}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={y(value)}
              y2={y(value)}
              className="stroke-ink-200"
              strokeWidth={1}
            />
            <text
              x={PADDING.left - 6}
              y={y(value) + 3}
              textAnchor="end"
              className="fill-ink-400 text-[9px]"
            >
              {Math.round(value)}
            </text>
          </g>
        ))}

        {/* Scope first, so the lines that matter more sit on top of it. */}
        <polyline
          points={path(scope)}
          fill="none"
          className="stroke-ink-300"
          strokeWidth={1.5}
          strokeDasharray="2 3"
        />

        <polyline
          points={points
            .map((point, index) => `${x(index)},${y(point.idealRemaining)}`)
            .join(" ")}
          fill="none"
          className="stroke-ink-400"
          strokeWidth={1.5}
          strokeDasharray="5 4"
        />

        <polyline
          points={path(remaining)}
          fill="none"
          className="stroke-brand-500"
          strokeWidth={2.5}
          strokeLinejoin="round"
        />

        {points.map((point, index) => (
          <circle
            key={point.date}
            cx={x(index)}
            cy={y(remaining(index))}
            r={3}
            className="fill-brand-500"
          >
            <title>
              {point.date}: {remaining(index)} {unit} remaining, {scope(index)} in scope
            </title>
          </circle>
        ))}

        <text
          x={PADDING.left}
          y={HEIGHT - 8}
          className="fill-ink-400 text-[9px]"
        >
          {data.startDate}
        </text>
        <text
          x={WIDTH - PADDING.right}
          y={HEIGHT - 8}
          textAnchor="end"
          className="fill-ink-400 text-[9px]"
        >
          {data.endDate}
        </text>
      </svg>

      <figcaption className="mt-2 flex flex-wrap gap-4 text-xs text-ink-500">
        <Key className="bg-brand-500" label={`Remaining ${unit}`} />
        <Key className="bg-ink-400" label="Ideal, from what was committed" dashed />
        <Key className="bg-ink-300" label="Scope — rises when work is added" dashed />
      </figcaption>
    </figure>
  );
}

function Key({
  className,
  label,
  dashed,
}: {
  className: string;
  label: string;
  dashed?: boolean;
}) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className={`inline-block h-0.5 w-5 ${className} ${dashed ? "opacity-70" : ""}`}
        aria-hidden
      />
      {label}
    </span>
  );
}
